import React, { useState, useEffect } from 'react';
import jsPDF from 'jspdf';
import './Pages.css';

const COVER_PERIODS = [
  { value: '2installments', label: 'Rest of Period' },
  { value: 'Monthly', label: 'Monthly' }
];

const ExtendPolicy = ({ policyId, setActiveMenu }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [policy, setPolicy] = useState(null);
  const [coverPeriod, setCoverPeriod] = useState('2installments');
  const [extensionData, setExtensionData] = useState({
    CommencementDate: '',
    ExpiryDate: ''
  });
  const [extensionResult, setExtensionResult] = useState(null);
  const [submissionError, setSubmissionError] = useState('');
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);

  // The Extend button always writes a fresh selectedPolicyId right before navigating here, so it
  // takes priority over the policyId prop (editPolicyId), which can be stale from an unrelated Edit Policy visit.
  const actualPolicyId = localStorage.getItem('selectedPolicyId') || policyId;

  useEffect(() => {
    console.log('ExtendPolicy - Received policyId:', actualPolicyId);
    if (actualPolicyId) {
      fetchPolicy();
    } else {
      console.error('ExtendPolicy - No policyId provided');
      setLoading(false);
      alert('No policy ID provided');
    }
  }, [actualPolicyId]);

  const fetchPolicy = async () => {
    try {
      console.log('Fetching policy with ID:', actualPolicyId);
      const response = await fetch(`https://main.victriedemos.com:3017/api/policies/${actualPolicyId}`);
      console.log('API Response status:', response.status);
      const data = await response.json();
      console.log('API Response data:', data);

      if (data.success) {
        setPolicy(data.data);
      } else {
        alert('Failed to load policy details: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error fetching policy:', err);
      alert('Failed to load policy details: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const calculateEstimatedExpiry = (commencementDateStr, period) => {
    if (period === '2installments') {
      // Rest of Period completes the original 12-month cycle, regardless of the new commencement date entered
      if (!policy?.CommencementDate) return '';
      const expiryDate = new Date(policy.CommencementDate);
      expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      expiryDate.setDate(expiryDate.getDate() - 1);
      return expiryDate.toISOString().split('T')[0];
    }

    if (!commencementDateStr) return '';
    const expiryDate = new Date(commencementDateStr);
    expiryDate.setMonth(expiryDate.getMonth() + 1);
    expiryDate.setDate(expiryDate.getDate() - 1);
    return expiryDate.toISOString().split('T')[0];
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setExtensionData({
      ...extensionData,
      [name]: value
    });
    setQuote(null);

    if (name === 'CommencementDate' && value) {
      setExtensionData(prev => ({
        ...prev,
        ExpiryDate: calculateEstimatedExpiry(value, coverPeriod)
      }));
    }
  };

  const handleCoverPeriodChange = (e) => {
    const value = e.target.value;
    setCoverPeriod(value);
    setQuote(null);
    if (extensionData.CommencementDate) {
      setExtensionData(prev => ({
        ...prev,
        ExpiryDate: calculateEstimatedExpiry(prev.CommencementDate, value)
      }));
    }
  };

  const handleGetQuote = async () => {
    if (!extensionData.CommencementDate) {
      alert('Please select a commencement date');
      return;
    }

    setLoadingQuote(true);
    setQuote(null);

    try {
      const response = await fetch('https://main.victriedemos.com:3017/api/get-extension-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policyId: actualPolicyId,
          coverPeriod
        })
      });

      const data = await response.json();

      if (data.success) {
        setQuote(data.quote);
      } else {
        alert(data.message || 'Failed to get quote');
      }
    } catch (err) {
      console.error('Error getting extension quote:', err);
      alert('Failed to get quote');
    } finally {
      setLoadingQuote(false);
    }
  };

  const handleNextStep = () => {
    setStep(2);
  };

  const handlePreviousStep = () => {
    setStep(1);
  };

  const handleSubmitExtension = async () => {
    if (!extensionData.CommencementDate) {
      alert('Please select a commencement date');
      return;
    }

    if (!quote) {
      alert('Please get a quote before submitting');
      return;
    }

    setSubmitting(true);
    setSubmissionError('');

    try {
      const userEmail = localStorage.getItem('userEmail');

      const response = await fetch('https://main.victriedemos.com:3017/api/create-extension', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          policyId: actualPolicyId,
          commencementDate: extensionData.CommencementDate,
          coverPeriod,
          doneBy: userEmail
        })
      });

      const data = await response.json();

      if (data.success) {
        setExtensionResult({
          expiryDate: data.expiryDate,
          grossPremium: data.grossPremium,
          referenceNote: data.referenceNote,
          proposalNumber: data.proposalNumber,
          commencementDate: extensionData.CommencementDate,
          coverPeriod
        });
      } else if (/proposal is not active/i.test(data.message || '')) {
        setSubmissionError('This policy has not yet been declared to the underwriting company, so it cannot be extended yet.');
      } else {
        setSubmissionError(data.message || 'Failed to create extension');
      }
    } catch (err) {
      console.error('Error creating extension:', err);
      setSubmissionError('An error occurred while creating extension');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinishExtension = () => {
    setExtensionResult(null);
    setActiveMenu('new-extensions');
  };

  const handleDownloadDebitNote = () => {
    if (!extensionResult || !policy) return;

    const doc = new jsPDF();
    const periodLabel = COVER_PERIODS.find(p => p.value === extensionResult.coverPeriod)?.label || extensionResult.coverPeriod;

    // Header
    doc.setFillColor(102, 126, 234);
    doc.rect(0, 0, 210, 32, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('ZAKIM INSURANCE AGENCY', 105, 15, { align: 'center' });
    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.text('DEBIT NOTE', 105, 24, { align: 'center' });

    doc.setTextColor(0, 0, 0);

    // Debit note number & date
    let y = 42;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Debit Note No:', 20, y);
    doc.setFont('helvetica', 'normal');
    doc.text(extensionResult.referenceNote || 'N/A', 60, y);

    doc.setFont('helvetica', 'bold');
    doc.text('Date Issued:', 130, y);
    doc.setFont('helvetica', 'normal');
    doc.text(new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }), 165, y);

    y += 4;
    doc.setDrawColor(220, 220, 220);
    doc.line(20, y, 190, y);
    y += 10;

    // Two-column section: Bill To / Policy Details
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To', 20, y);
    doc.text('Policy Details', 110, y);
    y += 7;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const billTo = [
      `${policy.FirstName || ''} ${policy.LastName || ''}`.trim() || 'N/A',
      policy.CustomerEmail || 'N/A',
      policy.Phone || 'N/A',
      `ID No: ${policy.IDNumber || 'N/A'}`,
      `KRA PIN: ${policy.KraPin || 'N/A'}`
    ];
    const policyDetails = [
      `Policy Number: ${policy.PolicyNumber || 'N/A'}`,
      `Registration: ${policy.Registration || 'N/A'}`,
      `Product: ${policy.ProductName || 'N/A'}`,
      `Cover Type: ${policy.CoverType || 'N/A'}`,
      extensionResult.proposalNumber ? `Proposal Number: ${extensionResult.proposalNumber}` : null
    ].filter(Boolean);

    const maxRows = Math.max(billTo.length, policyDetails.length);
    for (let i = 0; i < maxRows; i++) {
      if (billTo[i]) doc.text(billTo[i], 20, y + i * 6);
      if (policyDetails[i]) doc.text(policyDetails[i], 110, y + i * 6);
    }
    y += maxRows * 6 + 8;

    doc.setDrawColor(220, 220, 220);
    doc.line(20, y, 190, y);
    y += 10;

    // Extension period section
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Extension Period', 20, y);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const extensionInfo = [
      ['Cover Period:', periodLabel],
      ['New Commencement Date:', formatDate(extensionResult.commencementDate)],
      ['New Expiry Date:', formatDate(extensionResult.expiryDate)]
    ];
    extensionInfo.forEach(([label, value]) => {
      doc.setFont('helvetica', 'bold');
      doc.text(label, 20, y);
      doc.setFont('helvetica', 'normal');
      doc.text(value, 75, y);
      y += 6;
    });

    y += 8;

    // Amount table
    doc.setFillColor(102, 126, 234);
    doc.rect(20, y, 170, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Description', 24, y + 6);
    doc.text('Amount', 186, y + 6, { align: 'right' });
    y += 9;

    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(220, 220, 220);
    doc.rect(20, y, 170, 10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Extension Premium (${periodLabel})`, 24, y + 7);
    doc.text(formatCurrency(extensionResult.grossPremium), 186, y + 7, { align: 'right' });
    y += 10;

    doc.setFillColor(240, 242, 255);
    doc.rect(20, y, 170, 10, 'F');
    doc.setDrawColor(220, 220, 220);
    doc.rect(20, y, 170, 10);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Due', 24, y + 7);
    doc.text(formatCurrency(extensionResult.grossPremium), 186, y + 7, { align: 'right' });
    y += 20;

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.setFont('helvetica', 'normal');
    doc.text(`Reference Note: ${extensionResult.referenceNote || 'N/A'}`, 20, y);
    y += 12;
    doc.text('This is a system-generated debit note and does not require a signature.', 105, 285, { align: 'center' });

    doc.save(`DebitNote_${extensionResult.referenceNote || policy.PolicyNumber}.pdf`);
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      setActiveMenu('approved-policies');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading policy details...</p>
        </div>
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="page-container">
        <p>Policy is {policyId}</p>
        <div className="error-banner">Policy not found</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>🔄 Extend Policy</h1>
          <p className="page-subtitle">Extend the coverage period for policy {policy.PolicyNumber}</p>
        </div>
        <button className="back-btn" onClick={handleCancel}>
          ✕ Close
        </button>
      </div>

      <div className="form-container-narrow">
        {/* Progress Indicator */}
        <div className="step-indicators">
          <div className={`step-indicator ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
            <div className="step-number">{step > 1 ? '✓' : '1'}</div>
            <div className="step-label">Review Details</div>
          </div>
          <div className="step-line"></div>
          <div className={`step-indicator ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Set Extension Period</div>
          </div>
        </div>

        {/* Step 1: Review Policy Details */}
        {step === 1 && (
          <div className="step-content">
            <div className="step-header">
              <h2>📋 Review Policy Information</h2>
              <p className="step-description">Please review the policy details before proceeding with the extension</p>
            </div>
            
            {/* Policy Summary Card */}
            <div className="info-card highlight-card">
              <div className="card-header">
                <h3>📄 Policy Summary</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Policy Number</span>
                  <span className="info-value bold">{policy.PolicyNumber}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Product</span>
                  <span className="info-value">{policy.ProductName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Cover Type</span>
                  <span className="info-value">{policy.CoverType}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Premium Amount</span>
                  <span className="info-value bold amount-text">{formatCurrency(policy.GrossPremium)}</span>
                </div>
              </div>
            </div>

            {/* Client Details Card */}
            <div className="info-card">
              <div className="card-header">
                <h3>👤 Client Information</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Full Name</span>
                  <span className="info-value">{policy.FirstName} {policy.LastName}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email Address</span>
                  <span className="info-value">{policy.CustomerEmail}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone Number</span>
                  <span className="info-value">{policy.Phone}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">ID Number</span>
                  <span className="info-value">{policy.IDNumber}</span>
                </div>
              </div>
            </div>

            {/* Vehicle Details Card */}
            <div className="info-card">
              <div className="card-header">
                <h3>🚗 Vehicle Information</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Registration No.</span>
                  <span className="info-value bold">{policy.Registration}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Make & Model</span>
                  <span className="info-value">{policy.Make} {policy.Model}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Year of Manufacture</span>
                  <span className="info-value">{policy.YOM || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Chassis Number</span>
                  <span className="info-value">{policy.Chassis || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Current Coverage Period Card */}
            <div className="info-card warning-card">
              <div className="card-header">
                <h3>📅 Current Coverage Period</h3>
              </div>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Start Date</span>
                  <span className="info-value">{formatDate(policy.CommencementDate)}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">End Date</span>
                  <span className="info-value">{formatDate(policy.ExpiryDate)}</span>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                ✕ Cancel
              </button>
              <button type="button" className="btn-primary" onClick={handleNextStep}>
                Continue to Extension Dates →
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Set Extension Dates */}
        {step === 2 && (
          <div className="step-content">
            {extensionResult ? (
              <>
                <div className="step-header">
                  <h2>✅ Extension Confirmed</h2>
                  <p className="step-description">Review the details below and download the debit note for your records</p>
                </div>

                <div className="info-card highlight-card">
                  <div className="card-header">
                    <h3>📋 Extension Details</h3>
                  </div>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Policy Number</span>
                      <span className="info-value bold">{policy.PolicyNumber}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Registration</span>
                      <span className="info-value">{policy.Registration}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Cover Period</span>
                      <span className="info-value">{COVER_PERIODS.find(p => p.value === extensionResult.coverPeriod)?.label}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">New Commencement Date</span>
                      <span className="info-value">{formatDate(extensionResult.commencementDate)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">New Expiry Date</span>
                      <span className="info-value">{formatDate(extensionResult.expiryDate)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Premium</span>
                      <span className="info-value bold amount-text">{formatCurrency(extensionResult.grossPremium)}</span>
                    </div>
                    {extensionResult.referenceNote && (
                      <div className="info-item">
                        <span className="info-label">Reference Note</span>
                        <span className="info-value">{extensionResult.referenceNote}</span>
                      </div>
                    )}
                    {extensionResult.proposalNumber && (
                      <div className="info-item">
                        <span className="info-label">Proposal Number</span>
                        <span className="info-value">{extensionResult.proposalNumber}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={handleDownloadDebitNote}>
                    ⬇️ Download Debit Note
                  </button>
                  <button type="button" className="btn-primary btn-success" onClick={handleFinishExtension}>
                    ✓ Done
                  </button>
                </div>
              </>
            ) : (
              <>
            <div className="step-header">
              <h2>📅 Set Extension Period</h2>
              <p className="step-description">Define the new coverage period for this policy extension</p>
            </div>

            {/* Date Selection Card */}
            <div className="info-card highlight-card">
              <div className="card-header">
                <h3>🗓️ Extension Dates</h3>
              </div>
              
              <div className="form-row-spacing">
                <div className="form-group">
                  <label className="form-label-icon">
                    <span className="label-icon">📆</span>
                    New Commencement Date *
                  </label>
                  <input
                    type="date"
                    name="CommencementDate"
                    value={extensionData.CommencementDate}
                    onChange={handleDateChange}
                    className="form-input"
                    required
                  />
                  <small className="form-hint">Select the start date for the extended coverage period</small>
                </div>

                <div className="form-group">
                  <label className="form-label-icon">
                    <span className="label-icon">🔁</span>
                    Cover Period *
                  </label>
                  <select
                    name="CoverPeriod"
                    value={coverPeriod}
                    onChange={handleCoverPeriodChange}
                    className="form-input"
                  >
                    {COVER_PERIODS.map((period) => (
                      <option key={period.value} value={period.value}>{period.label}</option>
                    ))}
                  </select>
                  <small className="form-hint">Determines the extension duration and premium</small>
                </div>
              </div>

              <div className="form-row-spacing">
                <div className="form-group">
                  <label className="form-label-icon">
                    <span className="label-icon">📆</span>
                    New Expiry Date (estimated)
                  </label>
                  <input
                    type="date"
                    name="ExpiryDate"
                    value={extensionData.ExpiryDate}
                    className="form-input disabled-input"
                    readOnly
                    disabled
                  />
                  <small className="form-hint">Final expiry date and premium will be confirmed on submission</small>
                </div>
              </div>
            </div>

            {/* Get Quote */}
            <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleGetQuote}
                disabled={loadingQuote || !extensionData.CommencementDate}
              >
                {loadingQuote ? '⏳ Getting Quote...' : '💰 Get Quote'}
              </button>
            </div>

            {quote ? (
              <div className="info-card highlight-card">
                <div className="card-header">
                  <h3>💰 Premium Breakdown</h3>
                </div>
                <div className="info-grid">
                  {quote.basicPremium != null && (
                    <div className="info-item">
                      <span className="info-label">Basic Premium</span>
                      <span className="info-value">{formatCurrency(quote.basicPremium)}</span>
                    </div>
                  )}
                  {quote.phcf != null && (
                    <div className="info-item">
                      <span className="info-label">PHCF</span>
                      <span className="info-value">{formatCurrency(quote.phcf)}</span>
                    </div>
                  )}
                  {quote.trainingLevy != null && (
                    <div className="info-item">
                      <span className="info-label">Training Levy</span>
                      <span className="info-value">{formatCurrency(quote.trainingLevy)}</span>
                    </div>
                  )}
                  {quote.stampDuty != null && (
                    <div className="info-item">
                      <span className="info-label">Stamp Duty</span>
                      <span className="info-value">{formatCurrency(quote.stampDuty)}</span>
                    </div>
                  )}
                  <div className="info-item">
                    <span className="info-label">Gross Premium</span>
                    <span className="info-value bold amount-text">{formatCurrency(quote.grossPremium)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="info-banner success-banner">
                <span className="banner-icon">💰</span>
                <div className="banner-content">
                  <strong>Premium Amount</strong>
                  <p>Click "Get Quote" to preview the premium for the selected cover period before submitting.</p>
                </div>
              </div>
            )}

            {/* Policy Details Summary */}
            <div className="summary-box">
              <h4>📋 Extension Summary</h4>
              <div className="summary-row">
                <span>Policy Number:</span>
                <strong>{policy.PolicyNumber}</strong>
              </div>
              <div className="summary-row">
                <span>Client Name:</span>
                <strong>{policy.FirstName} {policy.LastName}</strong>
              </div>
              <div className="summary-row">
                <span>Registration:</span>
                <strong>{policy.Registration}</strong>
              </div>
              <div className="summary-row">
                <span>Current Policy Premium:</span>
                <strong className="amount-text">{formatCurrency(policy.GrossPremium)}</strong>
              </div>
              {extensionData.CommencementDate && (
                <>
                  <div className="summary-divider"></div>
                  <div className="summary-row highlight">
                    <span>Cover Period:</span>
                    <strong>{COVER_PERIODS.find(p => p.value === coverPeriod)?.label}</strong>
                  </div>
                  <div className="summary-row highlight">
                    <span>New Coverage Period (estimated):</span>
                    <strong>{formatDate(extensionData.CommencementDate)} - {formatDate(extensionData.ExpiryDate)}</strong>
                  </div>
                </>
              )}
              {quote && (
                <div className="summary-row highlight">
                  <span>Extension Premium:</span>
                  <strong className="amount-text">{formatCurrency(quote.grossPremium)}</strong>
                </div>
              )}
            </div>

            {submissionError && (
              <div className="error-banner">⚠️ {submissionError}</div>
            )}

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={handlePreviousStep}>
                ← Back to Details
              </button>
              <button
                type="button"
                className="btn-primary btn-success"
                onClick={handleSubmitExtension}
                disabled={submitting || !extensionData.CommencementDate || !quote}
              >
                {submitting ? (
                  <>⏳ Submitting Extension...</>
                ) : (
                  <>✓ Submit Extension Request</>
                )}
              </button>
            </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExtendPolicy;
